import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useNavigate, useMatch } from "react-router-dom";
import { useRegisterPageLocale } from "../i18n/LocaleProvider";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import type { TypographyProps } from "@mui/material";
import {
  Box,
  Container,
  Typography,
  Button,
  Avatar,
  Skeleton,
  Grid,
  Tooltip,
  IconButton,
  CircularProgress,
  Paper,
  Alert,
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TikTokIcon from "../components/icons/TikTokIcon";
import { useQuery } from "@tanstack/react-query";
import { alpha, styled } from "@mui/material/styles";
import {
  fetchPodcastMediaKit,
  fetchPodcastMediaKitExample,
  fetchProgramMediaKit,
  fetchProgramMediaKitByPath,
  type PodcastMediaKit as PodcastMediaKitType,
} from "../api/podcasts";
import { analytics, AnalyticsEvent } from "../services/analytics";
import { productBrand } from "../productBrand";
import { MediaKitYouTubeAnalyticsSection } from "./PodcastMediaKit/MediaKitYouTubeAnalyticsSection";
import { MediaKitYoutubeCatalogPdfAppendix } from "./PodcastMediaKit/MediaKitYoutubeCatalogPdfAppendix";
import { MediaKitInstagramAnalyticsSection } from "./PodcastMediaKit/MediaKitInstagramAnalyticsSection";
import { MediaKitTikTokAnalyticsSection } from "./PodcastMediaKit/MediaKitTikTokAnalyticsSection";
import { MediaKitPodscopeAnalyticsSection } from "./PodcastMediaKit/MediaKitPodscopeAnalyticsSection";
import { MediaKitPlatformSectionHeader } from "./PodcastMediaKit/MediaKitPlatformSectionHeader";
import { formatSelectedMonths } from "../components/MediaKitSettings";
import { MediaKitMetricsWindowContext } from "./PodcastMediaKit/MediaKitMetricsWindowCaption";
import apiService from "../services/api";
import { getS3Url, ensureDirectS3Url } from "../utils/imageUtils";
import {
  captureMediaKitElementToPdfBlob,
  MEDIA_KIT_PDF_ROOT_ID,
} from "./PodcastMediaKit/mediaKitPdfExport";

const IS_DEV = process.env.NODE_ENV === "development";
const PDF_PREGEN_DELAY_MS = IS_DEV ? 500 : 2000;

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  position: "relative",
  backgroundColor: theme.palette.background.default,
  paddingBottom: "4rem",
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  "&&": {
    color: theme.palette.primary.main,
    fontSize: "1.75rem",
    lineHeight: 1.2,
    fontWeight: 800,
    fontFamily: '"DM Serif Display", serif',
    textAlign: "center",
    margin: 0,
    overflow: "visible",
    maxWidth: "none",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "@media (min-width: 600px)": {
    "&&": {
      fontSize: "2.25rem",
      lineHeight: 1.2,
    },
  },
  "@media (min-width: 900px)": {
    "&&": {
      fontSize: "2.75rem",
    },
  },
}));

/** Matches StatCard surfaces: white tile, dark text, 1px divider border, card radius. */
const TimePeriodBadge = styled(Box)(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: "0.85rem",
  fontWeight: 500,
  fontFamily: '"DM Sans", sans-serif',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
  boxShadow: `0 1px 2px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.08)}`,
  margin: 0,
  padding: "0.45rem 16px",
  borderRadius: theme.shape.borderRadius,
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  "& [data-mediakit-pdf-time-period-label]": {
    display: "block",
    lineHeight: 1,
  },
  "@media (min-width: 600px)": {
    fontSize: "0.9rem",
    padding: "0.45rem 20px",
  },
}));

const PlatformIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.primary.main, 0.1),
    transform: "translateY(-2px)",
  },
}));

const FooterText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.85rem",
  fontFamily: '"DM Sans", sans-serif',
  margin: 0,
  padding: 0,
  overflow: "visible",
  textOverflow: "clip",
  maxWidth: "none",
}));

const FooterBrand = styled(Typography)(({ theme }) => ({
  "&&": {
    color: theme.palette.primary.main,
    fontSize: "1rem",
    fontWeight: 700,
    fontFamily: '"DM Serif Display", serif',
    cursor: "pointer",
    transition: "opacity 0.2s",
    margin: 0,
    padding: 0,
    overflow: "visible",
    textOverflow: "clip",
    maxWidth: "none",
  },
  "&:hover": {
    opacity: 0.7,
  },
}));

export interface PodcastMediaKitViewProps {
  mediaKitData: PodcastMediaKitType;
  company?: { name: string; imageS3Key?: string | null } | null;
  companyLoading: boolean;
}

/** Shared presentation for public media kit routes (company slug or program slug). */
export const PodcastMediaKitView: React.FC<PodcastMediaKitViewProps> = ({
  mediaKitData,
  company,
  companyLoading,
}) => {
  const { t } = useTranslation("mediaKit");
  const { companySlug } = useParams<{ companySlug?: string }>();
  const pdfRootId = MEDIA_KIT_PDF_ROOT_ID;
  const { podcast, platforms } = mediaKitData;
  const demoCompany = mediaKitData.demoCompany;
  const hasYoutubeAnalytics = Boolean(
    mediaKitData.analytics?.youtube &&
      Object.keys(mediaKitData.analytics.youtube).length > 0,
  );
  const showYoutubePdfVideoTable = Boolean(
    (mediaKitData.mediaKit?.enabledMetrics?.youtube?.longForm as
      | { showPdfVideoTable?: boolean }
      | undefined)?.showPdfVideoTable,
  );
  const { data: youtubeCatalog, isFetched: youtubeCatalogFetched } = useQuery({
    queryKey: [
      "media-kit-youtube-catalog",
      mediaKitData.podcast.slug,
      companySlug ?? "",
      (mediaKitData.selectedMonths ?? []).join(","),
      mediaKitData.program?.slug ?? "",
      "longForm",
    ],
    queryFn: () =>
      apiService.getPodcastMediaKitYoutubeCatalog(mediaKitData.podcast.slug, {
        months: mediaKitData.selectedMonths,
        programSlug: mediaKitData.program?.slug,
        companySlug,
        format: "longForm",
      }),
    enabled:
      showYoutubePdfVideoTable &&
      hasYoutubeAnalytics &&
      Boolean(mediaKitData.podcast.slug),
    staleTime: 5 * 60 * 1000,
  });
  const youtubeCatalogPending =
    showYoutubePdfVideoTable && hasYoutubeAnalytics && !youtubeCatalogFetched;
  const youtubeCatalogVideos = youtubeCatalog?.videos ?? [];

  const demoLogoSrc =
    mediaKitData.isDemo && demoCompany
      ? demoCompany.imageS3Key
        ? getS3Url(demoCompany.imageS3Key)
        : demoCompany.imageUrl || ""
      : "";

  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [devPdfPreviewOpen, setDevPdfPreviewOpen] = useState(false);
  const generatingRef = useRef(false);

  const pdfPreviewObjectUrl = useMemo(
    () => (pdfBlob ? URL.createObjectURL(pdfBlob) : null),
    [pdfBlob],
  );

  useEffect(() => {
    return () => {
      if (pdfPreviewObjectUrl) URL.revokeObjectURL(pdfPreviewObjectUrl);
    };
  }, [pdfPreviewObjectUrl]);

  const pdfSourceKey = useMemo(
    () =>
      [
        mediaKitData.podcast._id,
        mediaKitData.program?.slug ?? "",
        (mediaKitData.selectedMonths ?? []).join(","),
        company?.imageS3Key ?? "",
        showYoutubePdfVideoTable
          ? youtubeCatalogPending
            ? "yt-pending"
            : youtubeCatalogVideos.map((v) => v.id).join(",")
          : "yt-off",
      ].join("|"),
    [
      mediaKitData.podcast._id,
      mediaKitData.program?.slug,
      mediaKitData.selectedMonths,
      company?.imageS3Key,
      showYoutubePdfVideoTable,
      youtubeCatalogPending,
      youtubeCatalogVideos,
    ],
  );

  const downloadPdf = useCallback(
    (blob: Blob) => {
      const safeSlug = (podcast.slug || "mediakit").replace(
        /[^a-z0-9-_]+/gi,
        "-",
      );
      const filename = `mediakit-${safeSlug}-${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    [podcast.slug],
  );

  const handlePdfDownload = useCallback(() => {
    if (pdfBlob) {
      downloadPdf(pdfBlob);
      return;
    }
    const el = document.getElementById(pdfRootId);
    if (!el) return;
    setIsGeneratingPdf(true);
    captureMediaKitElementToPdfBlob(el)
      .then((blob) => {
        if (blob) {
          setPdfBlob(blob);
          downloadPdf(blob);
        }
      })
      .catch((err) => console.error("On-demand media kit PDF failed:", err))
      .finally(() => setIsGeneratingPdf(false));
  }, [pdfBlob, downloadPdf, pdfRootId]);

  const regeneratePdfPreview = useCallback(async () => {
    const el = document.getElementById(pdfRootId);
    if (!el) return;
    setIsGeneratingPdf(true);
    try {
      const blob = await captureMediaKitElementToPdfBlob(el);
      if (blob) setPdfBlob(blob);
    } catch (err) {
      console.error("Regenerate media kit PDF preview failed:", err);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [pdfRootId]);

  useEffect(() => {
    if (companyLoading || youtubeCatalogPending) {
      setPdfBlob(null);
      return;
    }
    setPdfBlob(null);
    generatingRef.current = false;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled || generatingRef.current) return;
      const el = document.getElementById(pdfRootId);
      if (!el) {
        return;
      }
      generatingRef.current = true;
      setIsGeneratingPdf(true);
      try {
        const blob = await captureMediaKitElementToPdfBlob(el);
        if (!cancelled) setPdfBlob(blob);
      } catch (err) {
        console.error("Pre-generate media kit PDF failed:", err);
      } finally {
        if (!cancelled) {
          setIsGeneratingPdf(false);
          generatingRef.current = false;
        }
      }
    }, PDF_PREGEN_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      generatingRef.current = false;
    };
  }, [companyLoading, youtubeCatalogPending, pdfSourceKey, pdfRootId]);

  const enabledMetrics = mediaKitData.mediaKit?.enabledMetrics;
  const showContentCounts = {
    youtubeLongForm: Boolean(
      (enabledMetrics?.youtube?.longForm as any)?.showContentCount,
    ),
    youtubeShorts: Boolean(
      (enabledMetrics?.youtube?.shorts as any)?.showContentCount,
    ),
    instagram: Boolean((enabledMetrics?.instagram as any)?.showContentCount),
    tiktok: Boolean((enabledMetrics?.tiktok as any)?.showContentCount),
    podscopeLongForm: Boolean(
      (enabledMetrics?.podscope as any)?.longForm?.showContentCount,
    ),
    podscopeStreaming: Boolean(
      (enabledMetrics?.podscope as any)?.streaming?.showContentCount,
    ),
  };
  const anyContentCountEnabled = Object.values(showContentCounts).some(Boolean);
  const contentCountsMonthsKey = (mediaKitData.selectedMonths ?? [])
    .slice()
    .sort()
    .join(",");
  const programSlugForCounts = mediaKitData.program?.slug;
  const { data: contentCountsData } = useQuery({
    queryKey: [
      "media-kit-content-counts",
      mediaKitData.podcast.slug,
      contentCountsMonthsKey,
      programSlugForCounts ?? "",
      companySlug ?? "",
    ],
    queryFn: () =>
      apiService.getPodcastMediaKitContentCounts(
        mediaKitData.podcast.slug,
        mediaKitData.selectedMonths ?? [],
        programSlugForCounts,
        companySlug,
      ),
    enabled: anyContentCountEnabled && Boolean(mediaKitData.podcast.slug),
    staleTime: 5 * 60 * 1000,
  });

  const youtubeLongFormCount =
    mediaKitData.analytics?.youtube?.longForm?.consumption?.contentCount ??
    contentCountsData?.youtube?.videos;
  const youtubeShortsCount =
    mediaKitData.analytics?.youtube?.shorts?.consumption?.contentCount ??
    contentCountsData?.youtube?.shorts;

  const body = (
    <>
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          pt: { xs: 4, sm: 6 },
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {mediaKitData.isDemo && mediaKitData.demoNoticeEs ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              {mediaKitData.demoNoticeEs}
            </Alert>
          ) : null}
          <Box
            id={pdfRootId}
            sx={{
              p: 3,
              boxSizing: "border-box",
            }}
          >
            <Box
              component="section"
              data-mediakit-pdf-section=""
              data-mediakit-pdf-fluid=""
              sx={{ mb: 5 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  width: "100%",
                }}
              >
                <Box sx={{ minWidth: 0, flexShrink: 0 }}>
                  {(mediaKitData.isDemo && demoCompany
                    ? companyLoading || demoLogoSrc
                    : companyLoading || company?.imageS3Key) && (
                    <Box
                      data-mediakit-pdf-keep-overflow=""
                      sx={{
                        width: 80,
                        height: 80,
                        overflow: "hidden",
                        flexShrink: 0,
                        borderRadius: "8px",
                        borderColor: "divider",
                        borderWidth: "1px",
                        borderStyle: "solid",
                      }}
                    >
                      {companyLoading && !mediaKitData.isDemo ? (
                        <Skeleton
                          variant="rectangular"
                          width={80}
                          height={80}
                          sx={{ borderRadius: 2 }}
                        />
                      ) : mediaKitData.isDemo && demoCompany && demoLogoSrc ? (
                        <img
                          src={ensureDirectS3Url(demoLogoSrc)}
                          alt={`${demoCompany.name} logo`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : company?.imageS3Key ? (
                        <img
                          src={getS3Url(company.imageS3Key)}
                          alt={`${company.name} logo`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            console.error("Error loading company image:", {
                              imageS3Key: company.imageS3Key,
                              url: getS3Url(company.imageS3Key),
                              error: e,
                            });
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                </Box>
                <Box data-mediakit-pdf-exclude="" sx={{ flexShrink: 0 }}>
                  <Button
                    variant="outlined"
                    onClick={handlePdfDownload}
                    disabled={isGeneratingPdf && !pdfBlob}
                    aria-label={t("downloadPdfAria")}
                    sx={(theme) => ({
                      color: "text.primary",
                      backgroundColor: "background.paper",
                      borderColor: "divider",
                      fontFamily: '"DM Sans", sans-serif',
                      fontWeight: 600,
                      textTransform: "none",
                      px: 2,
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.common.white, 0.08)
                            : alpha(theme.palette.primary.main, 0.04),
                      },
                    })}
                  >
                    {isGeneratingPdf && !pdfBlob ? (
                      <CircularProgress
                        size={22}
                        thickness={4}
                        sx={{ color: "primary.main" }}
                      />
                    ) : (
                      t("downloadPdf")
                    )}
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                  width: "100%",
                  maxWidth: "none",
                  overflow: "visible",
                }}
              >
                <Avatar
                  src={
                    ensureDirectS3Url(
                      getS3Url(
                        mediaKitData.program?.imageS3Key ?? podcast.imageS3Key,
                      ),
                    ) ||
                    podcast.imageUrl ||
                    undefined
                  }
                  alt={mediaKitData.program?.name || podcast.title}
                  sx={(theme) => ({
                    width: { xs: 120, sm: 140 },
                    height: { xs: 120, sm: 140 },
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? `0 8px 32px ${alpha(theme.palette.common.black, 0.45)}`
                        : "0 8px 32px rgba(0, 0, 0, 0.1)",
                  })}
                />
                <Box
                  component="h1"
                  sx={{
                    m: 0,
                    width: "100%",
                    maxWidth: "none",
                    overflow: "visible",
                    textAlign: "center",
                  }}
                >
                  <HeaderTitle data-mediakit-pdf-cap-hero-title="">
                    {mediaKitData.program?.name || podcast.title}
                  </HeaderTitle>
                </Box>
                {mediaKitData.program?.name &&
                mediaKitData.program.name !== podcast.title ? (
                  <Typography
                    component="p"
                    sx={{
                      m: 0,
                      mt: 0.5,
                      textAlign: "center",
                      color: "text.secondary",
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      fontWeight: 600,
                      maxWidth: "none",
                      overflow: "visible",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {podcast.title}
                  </Typography>
                ) : null}
                <Box
                  data-mediakit-pdf-exclude=""
                  sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                >
                  {platforms?.youtube && (
                    <Tooltip title={t("visit", { name: "YouTube" })}>
                      <PlatformIconButton
                        onClick={() =>
                          window.open(
                            platforms.youtube || "",
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                        size="medium"
                      >
                        <YouTubeIcon />
                      </PlatformIconButton>
                    </Tooltip>
                  )}
                  {platforms?.instagram && (
                    <Tooltip title={t("visit", { name: "Instagram" })}>
                      <PlatformIconButton
                        onClick={() =>
                          window.open(
                            platforms.instagram || "",
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                        size="medium"
                      >
                        <InstagramIcon />
                      </PlatformIconButton>
                    </Tooltip>
                  )}
                  {platforms?.tiktok && (
                    <Tooltip title={t("visit", { name: "TikTok" })}>
                      <PlatformIconButton
                        onClick={() =>
                          window.open(
                            platforms.tiktok || "",
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                        size="medium"
                      >
                        <TikTokIcon />
                      </PlatformIconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>

            {(mediaKitData.analytics?.youtube ||
              mediaKitData.analytics?.instagram ||
              mediaKitData.analytics?.tiktok ||
              mediaKitData.analytics?.podscope ||
              mediaKitData.paidBrandsByProgram?.some(
                (g) => g.brands.length > 0,
              )) && (
              <Box sx={{ mb: 4 }}>
                {mediaKitData.selectedMonths?.length > 0 && (
                  <Box
                    component="section"
                    data-mediakit-pdf-section=""
                    sx={{ display: "flex", justifyContent: "center", mb: 4 }}
                  >
                    <TimePeriodBadge data-mediakit-pdf-time-period-badge="">
                      <span data-mediakit-pdf-time-period-label="">
                        {formatSelectedMonths(mediaKitData.selectedMonths)}
                      </span>
                    </TimePeriodBadge>
                  </Box>
                )}

                {mediaKitData.analytics?.youtube &&
                  Object.keys(mediaKitData.analytics.youtube).length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Box
                        component="section"
                        data-mediakit-pdf-section=""
                        sx={{
                          pb: 1,
                          overflow: "visible",
                        }}
                      >
                        <MediaKitPlatformSectionHeader platform="youtube" />
                      </Box>
                      <MediaKitYouTubeAnalyticsSection
                        analytics={mediaKitData.analytics}
                        platformUrl={platforms?.youtube || null}
                        longFormContentCount={
                          showContentCounts.youtubeLongForm
                            ? youtubeLongFormCount
                            : undefined
                        }
                        shortsContentCount={
                          showContentCounts.youtubeShorts &&
                          youtubeShortsCount != null &&
                          youtubeShortsCount > 0
                            ? youtubeShortsCount
                            : undefined
                        }
                        podcastSlug={mediaKitData.podcast.slug}
                        companySlug={companySlug}
                        selectedMonths={mediaKitData.selectedMonths}
                        programSlug={mediaKitData.program?.slug}
                      />
                    </Box>
                  )}

                {mediaKitData.analytics?.instagram &&
                  Object.keys(mediaKitData.analytics.instagram).length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Box
                        component="section"
                        data-mediakit-pdf-section=""
                        sx={{
                          pb: 1,
                          overflow: "visible",
                        }}
                      >
                        <MediaKitPlatformSectionHeader
                          platform="instagram"
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <MediaKitInstagramAnalyticsSection
                        analytics={mediaKitData.analytics}
                        platformUrl={platforms?.instagram || null}
                        contentCount={
                          showContentCounts.instagram &&
                          contentCountsData?.instagram
                            ? contentCountsData.instagram.posts
                            : undefined
                        }
                      />
                    </Box>
                  )}

                {mediaKitData.analytics?.tiktok &&
                  Object.keys(mediaKitData.analytics.tiktok).length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Box
                        component="section"
                        data-mediakit-pdf-section=""
                        sx={{
                          pb: 1,
                          overflow: "visible",
                        }}
                      >
                        <MediaKitPlatformSectionHeader
                          platform="tiktok"
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <MediaKitTikTokAnalyticsSection
                        analytics={mediaKitData.analytics}
                        platformUrl={platforms?.tiktok || null}
                        contentCount={
                          showContentCounts.tiktok && contentCountsData?.tiktok
                            ? contentCountsData.tiktok.videos
                            : undefined
                        }
                      />
                    </Box>
                  )}

                {((mediaKitData.analytics?.podscope &&
                  Object.keys(mediaKitData.analytics.podscope).length > 0) ||
                  (mediaKitData.paidBrandsByProgram?.some(
                    (g) => g.brands.length > 0,
                  ) ??
                    false) ||
                  showContentCounts.podscopeLongForm ||
                  showContentCounts.podscopeStreaming) && (
                  <Box sx={{ mb: 4 }}>
                    <Box
                      component="section"
                      data-mediakit-pdf-section=""
                      sx={{
                        mb: 2,
                        pb: 1,
                        overflow: "visible",
                      }}
                    >
                      <MediaKitPlatformSectionHeader
                        platform="podscope"
                        titleSx={{ mb: 0 }}
                      />
                    </Box>
                    <MediaKitPodscopeAnalyticsSection
                      analytics={mediaKitData.analytics}
                      paidBrandsByProgram={mediaKitData.paidBrandsByProgram}
                      longFormContentCount={
                        showContentCounts.podscopeLongForm
                          ? contentCountsData?.youtube?.videos
                          : undefined
                      }
                      streamingContentCount={
                        showContentCounts.podscopeStreaming
                          ? contentCountsData?.streaming?.count
                          : undefined
                      }
                    />
                  </Box>
                )}
              </Box>
            )}

            {showYoutubePdfVideoTable ? (
              <MediaKitYoutubeCatalogPdfAppendix videos={youtubeCatalogVideos} />
            ) : null}

            <Box
              sx={{
                mt: 6,
                pt: 4,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                overflow: "visible",
              }}
            >
              {/* Native title avoids MUI Tooltip wrappers that html2canvas often clips. */}
              <Box
                title={t("goTo", { name: productBrand.name })}
                onClick={() => window.open(productBrand.marketingUrl, "_blank")}
                sx={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 0.75,
                  cursor: "pointer",
                  flexShrink: 0,
                  flexGrow: 0,
                  overflow: "visible",
                  maxWidth: "none",
                  width: "max-content",
                }}
              >
                <FooterText
                  {...({ component: "span" } as TypographyProps)}
                  sx={{
                    display: "inline",
                    flexShrink: 0,
                  }}
                >
                  {t("generatedOn", {
                    date: new Date().toLocaleDateString(
                      i18n.language?.startsWith("en") ? "en-US" : "es-CL",
                      {
                        day: "numeric",
                        month: "long",
                      },
                    ),
                  })}
                </FooterText>
                <FooterBrand
                  {...({ component: "span" } as TypographyProps)}
                  sx={{
                    display: "inline",
                    flexShrink: 0,
                  }}
                >
                  {productBrand.name}
                </FooterBrand>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {IS_DEV && (
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                mb: devPdfPreviewOpen ? 2 : 0,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t("devPreview")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDevPdfPreviewOpen((o) => !o)}
              >
                {devPdfPreviewOpen ? t("hide") : t("show")}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={isGeneratingPdf}
                onClick={regeneratePdfPreview}
              >
                {t("regenerate")}
              </Button>
            </Box>
            {devPdfPreviewOpen && (
              <Box
                sx={{
                  height: "min(85vh, 1000px)",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  bgcolor: "grey.100",
                }}
              >
                {pdfPreviewObjectUrl ? (
                  <iframe
                    title={t("pdfPreviewTitle")}
                    src={pdfPreviewObjectUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: 560, display: "block" }}
                  />
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {isGeneratingPdf
                        ? t("generatingPdf")
                        : t("noPdfYet")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Container>
      )}
    </>
  );

  return (
    <MediaKitMetricsWindowContext.Provider
      value={mediaKitData.mediaKit?.metricsWindow}
    >
      <PageContainer>{body}</PageContainer>
    </MediaKitMetricsWindowContext.Provider>
  );
};

const PodcastMediaKit: React.FC = () => {
  const { t } = useTranslation("mediaKit");
  const { slug, programSlug, companySlug, podcastSlug } = useParams<{
    slug?: string;
    programSlug?: string;
    companySlug?: string;
    podcastSlug?: string;
  }>();
  const navigate = useNavigate();
  const companyMediaKitExampleMatch = useMatch("/:companySlug/media-kit");
  const examplePathCompanySlug =
    companyMediaKitExampleMatch?.params.companySlug;
  const isCompanyMediaKitExampleRoute = Boolean(companyMediaKitExampleMatch);
  const isPodscopeMediaKitExample =
    isCompanyMediaKitExampleRoute && examplePathCompanySlug === "podscope";
  const isInvalidCompanyMediaKitExample =
    isCompanyMediaKitExampleRoute && examplePathCompanySlug !== "podscope";

  const isProgramPathRoute = Boolean(companySlug && podcastSlug && programSlug);
  const isLegacyProgramaRoute = Boolean(programSlug && !companySlug);
  useRegisterPageLocale(
    isPodscopeMediaKitExample
      ? "podscope"
      : companySlug ?? examplePathCompanySlug ?? null,
  );

  const {
    data: mediaKitData,
    isLoading,
    error,
  } = useQuery<PodcastMediaKitType>({
    queryKey: isPodscopeMediaKitExample
      ? ["podcast-media-kit-example"]
      : isProgramPathRoute
        ? [
            "podcast-media-kit-program-path",
            companySlug,
            podcastSlug,
            programSlug,
          ]
        : isLegacyProgramaRoute
          ? ["podcast-media-kit-program", programSlug]
          : ["podcast-media-kit", slug, companySlug ?? ""],
    queryFn: () => {
      if (isPodscopeMediaKitExample) {
        return fetchPodcastMediaKitExample();
      }
      if (isProgramPathRoute) {
        return fetchProgramMediaKitByPath(
          companySlug!,
          podcastSlug!,
          programSlug!,
        );
      }
      if (isLegacyProgramaRoute) {
        return fetchProgramMediaKit(programSlug!);
      }
      return fetchPodcastMediaKit(slug!, companySlug ?? undefined);
    },
    enabled: isInvalidCompanyMediaKitExample
      ? false
      : isPodscopeMediaKitExample
        ? true
        : isProgramPathRoute
          ? Boolean(companySlug && podcastSlug && programSlug)
          : isLegacyProgramaRoute
            ? Boolean(programSlug)
            : Boolean(slug),
    retry: 1,
  });

  // When the payload already carries company data (program-path route), use it directly.
  const companyFromPayload = mediaKitData?.company ?? null;

  const resolvedPodcastSlug =
    mediaKitData?.podcast?.slug ||
    (isProgramPathRoute
      ? podcastSlug
      : !isLegacyProgramaRoute
        ? slug
        : undefined);

  const { data: podcastData } = useQuery({
    queryKey: ["podcast-by-slug", resolvedPodcastSlug],
    queryFn: async () => {
      if (!resolvedPodcastSlug) return null;
      return await apiService.getPodcastBySlug(resolvedPodcastSlug);
    },
    enabled:
      Boolean(resolvedPodcastSlug) &&
      !isPodscopeMediaKitExample &&
      !companyFromPayload,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Get companyId from podcast data - supports both new companyIds array and legacy companyId
  const companyId = companyFromPayload
    ? null
    : (podcastData as any)?.companyIds?.[0] || (podcastData as any)?.companyId;

  // Fetch company details (only when the payload did not already supply company)
  const { data: fetchedCompany, isLoading: fetchedCompanyLoading } = useQuery({
    queryKey: ["company", companyId],
    queryFn: async () => {
      if (!companyId) return null;
      try {
        return await apiService.getCompanyById(companyId);
      } catch (error) {
        console.error("Error fetching company:", error);
        return null;
      }
    },
    enabled: !!companyId && !isPodscopeMediaKitExample,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    retry: 1,
  });

  const company = companyFromPayload ?? fetchedCompany ?? null;
  const companyLoading = companyFromPayload ? false : fetchedCompanyLoading;

  // Track page view
  useEffect(() => {
    if (mediaKitData) {
      analytics.track(AnalyticsEvent.VIEW_PODCAST, {
        podcastId: mediaKitData.podcast._id,
        podcastSlug: mediaKitData.podcast.slug,
        pageType: "media-kit",
        ...(mediaKitData.program?.slug
          ? { programSlug: mediaKitData.program.slug }
          : {}),
      });
    }
  }, [mediaKitData]);

  if (isInvalidCompanyMediaKitExample) {
    return (
      <PageContainer>
        <Box sx={{ position: "relative", zIndex: 1, pt: { xs: 8, sm: 12 } }}>
          <Container
            maxWidth="lg"
            sx={{ px: { xs: 2, sm: 3, md: 4 }, textAlign: "center" }}
          >
            <HeaderTitle variant="h4" gutterBottom>
              {t("pageNotFound")}
            </HeaderTitle>
            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
              {t("noExample")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/")}
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
              }}
            >
              {t("backHome")}
            </Button>
          </Container>
        </Box>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Box sx={{ position: "relative", zIndex: 1, pt: { xs: 4, sm: 6 } }}>
          <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            {/* Company Logo Skeleton */}
            <Skeleton
              variant="rectangular"
              width={80}
              height={80}
              sx={{ mb: 3 }}
            />

            {/* Header Skeleton */}
            <Box
              sx={{
                mb: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Skeleton variant="circular" width={140} height={140} />
              <Skeleton
                variant="text"
                width={300}
                height={48}
                sx={{ maxWidth: "90%" }}
              />
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Skeleton variant="circular" width={44} height={44} />
                <Skeleton variant="circular" width={44} height={44} />
              </Box>
            </Box>

            {/* Time Period Badge Skeleton */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <Skeleton
                variant="rectangular"
                width={160}
                height={40}
                sx={{ borderRadius: 5 }}
              />
            </Box>

            {/* YouTube Section Skeleton */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={100} height={32} />
              </Box>
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                    <Skeleton
                      variant="rectangular"
                      height={100}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Instagram Section Skeleton */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={100} height={32} />
              </Box>
              <Grid container spacing={2}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                    <Skeleton
                      variant="rectangular"
                      height={100}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Footer Skeleton */}
            <Box
              sx={{
                mt: 6,
                pt: 4,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Skeleton variant="text" width={200} height={24} />
            </Box>
          </Container>
        </Box>
      </PageContainer>
    );
  }

  if (error || !mediaKitData) {
    return (
      <PageContainer>
        <Box sx={{ position: "relative", zIndex: 1, pt: { xs: 8, sm: 12 } }}>
          <Container
            maxWidth="lg"
            sx={{ px: { xs: 2, sm: 3, md: 4 }, textAlign: "center" }}
          >
            <HeaderTitle variant="h4" gutterBottom>
              {t("podcastNotFound")}
            </HeaderTitle>
            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
              {t("podcastNotFoundBody")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/")}
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
              }}
            >
              {t("backHome")}
            </Button>
          </Container>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PodcastMediaKitView
      mediaKitData={mediaKitData}
      company={isPodscopeMediaKitExample ? null : (company ?? null)}
      companyLoading={isPodscopeMediaKitExample ? false : companyLoading}
    />
  );
};

export default PodcastMediaKit;
